import React, { useCallback } from 'react';
import {
  AddressAccordion,
  CareTeamAccordion,
  DocumentsAccordion,
  EmergencyContactAccordion,
  InsuranceAccordion,
  PatientProfileGeneralInfo,
  UpcomingSessionsAccordion,
  useBreakpoint,
  PatientDiagnosisAccordion,
  HeightAndWeightAccordian,
  FamilyHistoryAccordian,
  MedicationAccordian,
  AllergiesAccordian,
  MedicalConditionsAccordion
} from '@uplift/ui-v2';
import { usePatientDetails } from './PatientDetails.hooks';
import { PsychiatryReferralFlow } from './PsychiatryReferralFlow';
import { useCurrentuser } from '@uplift/contexts';
import { PatientDocument } from '@uplift/api';
import { ProviderMedicalRole, UserStatus } from '@uplift/types';
import { useIsSmallTablet } from '../Layouts';
import { useIsActiveMatchBetweenPatientAndProvider } from '@uplift/queries';

export type GeneralPatientDetailsProps = {
  patientId: string;
  expanded?: boolean;
  setExpanded?: (expanded: boolean) => void;
};

export const GeneralPatientDetails: React.VFC<GeneralPatientDetailsProps> = ({ patientId, expanded, setExpanded }) => {
  const data = usePatientDetails(patientId, { suspense: true });
  const breakpoints = useBreakpoint();
  const isSmallTablet = useIsSmallTablet();
  const compact = !breakpoints.md;
  // expandable only on SM
  const expandPlacement = isSmallTablet ? 'top' : 'left';

  const handleToggle = useCallback(() => {
    return setExpanded(!expanded);
  }, [ expanded, setExpanded ]);

  return (
    <PatientProfileGeneralInfo
      user={data.user}
      profile={data.userProfile}
      contacts={data.contacts ?? []}
      compact={compact}
      expanded={expanded}
      onExpandToggle={handleToggle}
      expandPlacement={expandPlacement}
    />
  );
};


export type AccordionPatientDetailsProps = {
  patientId: string;
  onDocumentSelect?: (document: PatientDocument) => void;
  onViewAllDocuments?: () => void;
};

export const AccordionsPatientDetails: React.VFC<AccordionPatientDetailsProps> = ({ patientId, onDocumentSelect, onViewAllDocuments }) => {
  const me = useCurrentuser();
  const breakpoints = useBreakpoint();
  const data = usePatientDetails(patientId, { suspense: true });
  const isActiveMatch = useIsActiveMatchBetweenPatientAndProvider(patientId, me.id, { suspense: true });
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;

  return (
    <>
      <CareTeamAccordion
        currentProviderId={me.id}
        assets={data.assetsById}
        activeMatches={data.activeMatches}
        archivedMatches={data.archivedMatches}
        additionalContent={(
          <PsychiatryReferralFlow patientId={patientId} />
        )}
      />
      {Boolean(isActiveMatch && data.upcomingSessions?.length) && (
        <UpcomingSessionsAccordion sessions={data.upcomingSessions} />
      )}
      <React.Suspense fallback={null}>
        <PatientDiagnosisAccordion patientId={patientId} />
      </React.Suspense>

      <MedicationAccordian patientId={patientId} editable={isPsychiatrist && isActiveMatch} />
      <AllergiesAccordian patientId={patientId} editable={isPsychiatrist && isActiveMatch} />

      <MedicalConditionsAccordion patientId={patientId} editable={isActiveMatch} />
      <HeightAndWeightAccordian patientId={patientId} editable={isPsychiatrist && isActiveMatch} />
      <FamilyHistoryAccordian patientId={patientId} />

      <InsuranceAccordion
        insurances={(
          data.user.status === UserStatus.regular
            ? data.insurances
            : undefined
        )}
      />
      <AddressAccordion address={data.userProfile.address} state={data.addressState} county={data.addressCounty} />
      <EmergencyContactAccordion emergencyContact={data.emergencyContact} />
      {breakpoints.sm && (
        <DocumentsAccordion
          patientId={patientId}
          onSelect={onDocumentSelect}
          onViewAllDocuments={onViewAllDocuments}
        />
      )}
    </>
  );
};
